import { useState } from 'react';
import round from '../../../helpers/round';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const HansenResultHelpfulValues = ({ result }) => {
  const { fndWidth, fndLength, eccentricityX, eccentricityY, phi, cohesion, caCoefficient, df } = result.input;

  const {
    canUserSeeCalculationSteps,
    effectiveFndWidth,
    effectiveFndLength,
    effectiveFndRatio,
    effectiveFndArea,
    isInclinedLoading,
    isPhiZero,
    isWidthLargerThanLength,
    fndRatio,
    IsfndRatioOver2,
    isPhiOver34,
    phips,
    ca,
    dfOverFndWidth,
    dfOverFndLength,
    isDfOverFndWidthGreaterThan1,
    isDfOverFndLengthGreaterThan1,
    k,
    kb,
    kl,
  } = result.output;

  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const showBLstuff = isInclinedLoading && !isPhiZero;
  const [showEffectiveFndWidthDetails, setShowEffectiveFndWidthDetails] = useState(false);
  const [showEffectiveFndLengthDetails, setShowEffectiveFndLengthDetails] = useState(false);
  const [showEffectiveFndAreaDetails, setShowEffectiveFndAreaDetails] = useState(false);
  const [showEffectiveFndRatioDetails, setShowEffectiveFndRatioDetails] = useState(false);
  const [showBaseAdhesionDetails, setShowBaseAdhesionDetails] = useState(false);
  const [showPlaneStrainFrictionAngleDetails, setShowPlaneStrainFrictionAngleDetails] = useState(false);
  const [showDepthFactorKDetails, setShowDepthFactorKDetails] = useState(false);
  const [showDepthFactorKbDetails, setShowDepthFactorKbDetails] = useState(false);
  const [showDepthFactorKlDetails, setShowDepthFactorKlDetails] = useState(false);

  return (
    <Box sx={{ my: 2, textAlign: 'justify' }}>
      <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
        <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
          Calculation of Intermediate Hansen Values
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
          This section computes the intermediate values required before final Hansen bearing-capacity results are evaluated. It
          includes effective foundation dimensions (B', L'), effective area and ratio, base adhesion, plane strain friction angle,
          and depth-factor coefficients.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Effective Foundation Width (B') */}
          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(46,125,50,0.04)',
              alignItems: 'flex-start', // keep icon & action pinned to top
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24, // fixed width for left icon column
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                // fix the action column width so the button never shifts
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' }, // let message grow downward
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setShowEffectiveFndWidthDetails((v) => !v)}
                  sx={{ width: 32, height: 32 }} // matches action width above
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: showEffectiveFndWidthDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <b>Effective Foundation Width (B') = {round(effectiveFndWidth, 2)}m</b>
            </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showEffectiveFndWidthDetails} unmountOnExit>
                <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                  Effective foundation width B' is computed first. If B' becomes larger than L', B' and L' are reordered for
                  consistency.
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  B'= B-2e<sub>y</sub>
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  B'= {round(fndWidth, 2)}-2({round(eccentricityY, 2)})
                </Typography>
                {isWidthLargerThanLength ? (
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    B'= {round(effectiveFndLength, 2)}m
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    B'= {round(effectiveFndWidth, 2)}m
                  </Typography>
                )}
                {isWidthLargerThanLength && (
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    Since B'&gt; L'
                  </Typography>
                )}
                {isWidthLargerThanLength && (
                  <Typography variant="body2" color="text.secondary">
                    B'= {round(effectiveFndWidth, 2)}m
                  </Typography>
                )}
              </Collapse>
            )}
          </Alert>
          {/* Effective Foundation Length (L') */}
          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(46,125,50,0.04)',
              alignItems: 'flex-start', // keep icon & action pinned to top
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24, // fixed width for left icon column
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                // fix the action column width so the button never shifts
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' }, // let message grow downward
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setShowEffectiveFndLengthDetails((v) => !v)}
                  sx={{ width: 32, height: 32 }} // matches action width above
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: showEffectiveFndLengthDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <b>Effective Foundation Length (L') = {round(effectiveFndLength, 2)}m</b>
            </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showEffectiveFndLengthDetails} unmountOnExit>
                <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                  Effective foundation length L' follows the same reordering logic when B' exceeds L'.
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  L'= L-2e<sub>x</sub>
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  L'= {round(fndLength, 2)}-2({round(eccentricityX, 2)})
                </Typography>
                {isWidthLargerThanLength ? (
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    L'= {round(effectiveFndWidth, 2)}m
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    L'= {round(effectiveFndLength, 2)}m
                  </Typography>
                )}
                {isWidthLargerThanLength && (
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    Since L'&lt; B'
                  </Typography>
                )}
                {isWidthLargerThanLength && (
                  <Typography variant="body2" color="text.secondary">
                    L'= {round(effectiveFndLength, 2)}m
                  </Typography>
                )}
              </Collapse>
            )}
          </Alert>
          {/* Effective Foundation Area (Af) */}
          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(46,125,50,0.04)',
              alignItems: 'flex-start', // keep icon & action pinned to top
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24, // fixed width for left icon column
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                // fix the action column width so the button never shifts
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' }, // let message grow downward
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setShowEffectiveFndAreaDetails((v) => !v)}
                  sx={{ width: 32, height: 32 }} // matches action width above
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: showEffectiveFndAreaDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <b>
                Effective Foundation Area (A<sub>f</sub>) = {round(effectiveFndArea, 2)}m<sup>2</sup>
              </b>
            </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showEffectiveFndAreaDetails} unmountOnExit>
                <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                  Effective area is obtained directly from the effective width and effective length.
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  A<sub>f</sub>= B'L'
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  A<sub>f</sub>= ({round(effectiveFndWidth, 2)})({round(effectiveFndLength, 2)})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A<sub>f</sub>= {round(effectiveFndArea, 2)}m<sup>2</sup>
                </Typography>
              </Collapse>
            )}
          </Alert>
          {/* Effective Foundation Ratio (B'/L') */}
          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(46,125,50,0.04)',
              alignItems: 'flex-start', // keep icon & action pinned to top
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24, // fixed width for left icon column
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                // fix the action column width so the button never shifts
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' }, // let message grow downward
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setShowEffectiveFndRatioDetails((v) => !v)}
                  sx={{ width: 32, height: 32 }} // matches action width above
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: showEffectiveFndRatioDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <b>Effective Foundation Ratio (B'/L') = {round(effectiveFndRatio, 2)}</b>
            </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showEffectiveFndRatioDetails} unmountOnExit>
                <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                  Effective ratio is computed from the effective foundation width and length.
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  B'/L'= ({round(effectiveFndWidth, 2)})/({round(effectiveFndLength, 2)})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  B'/L'= {round(effectiveFndRatio, 2)}
                </Typography>
              </Collapse>
            )}
          </Alert>
          {/* Base adhesion (Ca) */}
          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(46,125,50,0.04)',
              alignItems: 'flex-start', // keep icon & action pinned to top
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24, // fixed width for left icon column
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                // fix the action column width so the button never shifts
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' }, // let message grow downward
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setShowBaseAdhesionDetails((v) => !v)}
                  sx={{ width: 32, height: 32 }} // matches action width above
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: showBaseAdhesionDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <b>
                Base adhesion (C<sub>a</sub>) = {round(ca, 2)} kPa
              </b>
            </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showBaseAdhesionDetails} unmountOnExit>
                <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                  Base adhesion is calculated from cohesion and the cohesion reduction factor.
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  C<sub>a</sub>= (c)(cohesion reduction factor)
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  C<sub>a</sub>= ({round(cohesion, 2)})({round(caCoefficient, 2)})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  C<sub>a</sub>= {round(ca, 2)} kPa
                </Typography>
              </Collapse>
            )}
          </Alert>
          {/* Plane Strain Friction Angle (phi_ps) */}
          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={{
              backgroundColor: 'rgba(46,125,50,0.04)',
              alignItems: 'flex-start', // keep icon & action pinned to top
              '& .MuiAlert-icon': {
                alignSelf: 'flex-start',
                mt: 0.25,
                minWidth: 24, // fixed width for left icon column
              },
              '& .MuiAlert-action': {
                alignSelf: 'flex-start',
                mt: 0.25,
                // fix the action column width so the button never shifts
                width: 32,
                justifyContent: 'center',
                p: 0,
                ml: 1,
              },
              '& .MuiAlert-message': { width: '100%' }, // let message grow downward
            }}
            action={
              canUserSeeCalculationSteps ? (
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => setShowPlaneStrainFrictionAngleDetails((v) => !v)}
                  sx={{ width: 32, height: 32 }} // matches action width above
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: showPlaneStrainFrictionAngleDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  />
                </IconButton>
              ) : (
                <Tooltip
                  title={
                    <span>
                      Upgrade to unlock calculation details.{' '}
                      <Link
                        href="/pricing"
                        target="_blank"
                        rel="noopener"
                        style={{ color: '#90caf9', textDecoration: 'underline' }}
                      >
                        See plans
                      </Link>
                    </span>
                  }
                  leaveDelay={500}
                  arrow
                >
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 20,
                      color: 'text.disabled',
                      cursor: 'not-allowed',
                      mr: 1,
                      mt: 0.5,
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <b>
                Plane Strain Friction Angle (&phi;<sub>ps</sub>) = {round(IsfndRatioOver2 && isPhiOver34 ? phips : phi, 2)}
              </b>
            </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showPlaneStrainFrictionAngleDetails} unmountOnExit>
                <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                  If L/B &gt;2 and &phi; &gt;34&deg; &rarr; Use &phi;<sub>ps</sub>
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  L/B = {round(fndLength, 2)}/{round(fndWidth, 2)} = {round(fndRatio, 2)}
                  {IsfndRatioOver2 ? <span>&gt;2 (OK)</span> : <span>&le;2 (NOT OK)</span>}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  {phi > 34 ? (
                    <span>&phi; = {round(phi, 2)}&deg; &gt; 34&deg; (OK)</span>
                  ) : (
                    <span>&phi; = {round(phi, 2)}&deg;&le;34&deg; (NOT OK)</span>
                  )}
                </Typography>
                {IsfndRatioOver2 && isPhiOver34 ? (
                  <>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                      Both conditions are satisfied simultaneously. Use &phi;<sub>ps</sub> as follows:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                      &phi;<sub>ps</sub> = 1.5&phi; - 17&deg;
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                      &phi;<sub>ps</sub> = 1.5({round(phi, 2)}&deg;) - 17&deg;
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      &phi;<sub>ps</sub> = {round(phips, 2)}&deg;
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                      Both conditions are not satisfied simultaneously. Use &phi; as follows:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      &phi;<sub>ps</sub> = &phi; = {round(phi, 2)}
                    </Typography>
                  </>
                )}
              </Collapse>
            )}
          </Alert>
          {!showBLstuff && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowDepthFactorKDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showDepthFactorKDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>Depth factor coefficient (k) = {round(k, 2)}</b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showDepthFactorKDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    D<sub>f</sub> / B &le; 1 &rarr; k = D<sub>f</sub> / B{' '}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    D<sub>f</sub> / B &gt; 1 &rarr; k = arctan (D<sub>f</sub> / B)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    k in radians.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    Since D<sub>f</sub> / B = {round(df, 2)} / {round(fndWidth, 2)} = {round(dfOverFndWidth, 2)}
                    {isDfOverFndWidthGreaterThan1 ? <span> &gt; 1</span> : <span> &le; 1</span>}
                  </Typography>
                  {isDfOverFndWidthGreaterThan1 ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        k = arctan (D<sub>f</sub> / B)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        k = arctan ({round(df, 2)} / {round(fndWidth, 2)})
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        k = {round(k, 2)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        k = D<sub>f</sub> / B = {round(df, 2)} / {round(fndWidth, 2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        k = {round(k, 2)}
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
          )}
          {showBLstuff && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowDepthFactorKbDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showDepthFactorKbDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Depth factor coefficient (k<sub>B</sub>) = {round(kb, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showDepthFactorKbDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    D<sub>f</sub> / B &le; 1 &rarr; k<sub>B</sub> = D<sub>f</sub> / B{' '}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    D<sub>f</sub> / B &gt; 1 &rarr; k<sub>B</sub> = arctan (D<sub>f</sub> / B)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    k<sub>B</sub> in radians.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    Since D<sub>f</sub> / B = {round(df, 2)} / {round(fndWidth, 2)} = {round(dfOverFndWidth, 2)}{' '}
                    {isDfOverFndWidthGreaterThan1 ? <span> &gt; 1</span> : <span> &le; 1</span>}
                  </Typography>
                  {isDfOverFndWidthGreaterThan1 ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        k<sub>B</sub> = arctan (D<sub>f</sub> / B)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        k<sub>B</sub> = arctan ({round(df, 2)} / {round(fndWidth, 2)})
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        k<sub>B</sub> = {round(kb, 2)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        k<sub>B</sub> = D<sub>f</sub> / B = {round(df, 2)} / {round(fndWidth, 2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        k<sub>B</sub> = {round(kb, 2)}
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
          )}
          {showBLstuff && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowDepthFactorKlDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showDepthFactorKlDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <b>
                  Depth factor coefficient (k<sub>L</sub>) = {round(kl, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showDepthFactorKlDetails} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
                    D<sub>f</sub> / L &le; 1 &rarr; k<sub>L</sub> = D<sub>f</sub> / L{' '}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    D<sub>f</sub> / L &gt; 1 &rarr; k<sub>L</sub> = arctan (D<sub>f</sub> / L)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    k<sub>L</sub> in radians.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    Since D<sub>f</sub> / L = {round(df, 2)} / {round(fndLength, 2)} = {round(dfOverFndLength, 2)}{' '}
                    {isDfOverFndWidthGreaterThan1 ? <span> &gt; 1</span> : <span> &le; 1</span>}
                  </Typography>
                  {isDfOverFndLengthGreaterThan1 ? (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        k<sub>L</sub> = arctan (D<sub>f</sub> / L)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        k<sub>L</sub> = arctan ({round(df, 2)} / {round(fndLength, 2)})
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        k<sub>L</sub> = {round(kl, 2)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        k<sub>L</sub> = D<sub>f</sub> / L = {round(df, 2)} / {round(fndLength, 2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        k<sub>L</sub> = {round(kl, 2)}
                      </Typography>
                    </>
                  )}
                </Collapse>
              )}
            </Alert>
          )}
        </Box>
      </Alert>
    </Box>
  );
};
export default HansenResultHelpfulValues;
